import { useEffect, useRef } from "react";

/**
 * Minimum time an asset stays on screen before the stream may move off it.
 * Annotations arrive unpredictably, so without a floor the carousel flickers
 * through assets as they are mentioned.
 */
export const MIN_DWELL_MS = 980;

interface StreamFollowOptions {
  /** Asset the newest annotation points at, or null when there is nothing to follow. */
  targetIndex: number | null;
  activeIndex: number;
  onSwitch: (index: number) => void;
  /** False once the viewer has taken manual control — suspends following entirely. */
  enabled: boolean;
}

/**
 * Follow a streaming answer across assets without thrashing the viewer.
 *
 * A target that arrives while the current asset is still inside its dwell is
 * held as *pending* and overwritten by anything newer, so a burst of
 * annotations collapses to wherever the stream actually ended up rather than
 * flipping through every asset on the way.
 */
export function useStreamFollow({ targetIndex, activeIndex, onSwitch, enabled }: StreamFollowOptions): void {
  const lastSwitchAt = useRef(0);
  const pending = useRef<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read through refs so the effect depends only on the target, not on a
  // callback identity or an index that this hook itself changes.
  const onSwitchRef = useRef(onSwitch);
  const activeRef = useRef(activeIndex);
  onSwitchRef.current = onSwitch;
  activeRef.current = activeIndex;

  const clear = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => {
    if (!enabled) {
      clear();
      pending.current = null;
      return;
    }
    // Nothing to follow, or the stream is already where the viewer is. Either
    // way a switch queued earlier is now stale — the stream moved on and came
    // back — so it is dropped rather than left to fire into an asset the
    // response has since left.
    if (targetIndex === null || targetIndex === activeRef.current) {
      pending.current = null;
      clear();
      return;
    }

    const commit = (index: number) => {
      lastSwitchAt.current = Date.now();
      pending.current = null;
      onSwitchRef.current(index);
    };

    const waited = Date.now() - lastSwitchAt.current;
    if (waited >= MIN_DWELL_MS) {
      commit(targetIndex);
      return;
    }

    // Inside the dwell: remember where the stream wants to be and go there once
    // it expires. A newer target simply replaces this one.
    pending.current = targetIndex;
    if (timer.current === null) {
      timer.current = setTimeout(() => {
        timer.current = null;
        const next = pending.current;
        if (next !== null && next !== activeRef.current) commit(next);
      }, MIN_DWELL_MS - waited);
    }
  }, [targetIndex, enabled]);

  useEffect(() => clear, []);
}
